/**
 * ConnectionActionsMenu - shared row-actions menu for JSON-backed
 * connection rows (service-provider / agent / MCP).
 *
 * All three connection families live inside connections.json and share the
 * same affordances (Edit, Delete) plus identical visual chrome, so the menu
 * is factored here and the parent passes `kind` along with `id` /
 * `displayName` to a single pair of callbacks. The parent decides what
 * happens \u2014 typically opening a confirmation dialog or an edit dialog \u2014 and
 * routes the action to the right `useConnectionsEditor` handler based on
 * `target.kind`.
 */

import {
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@coreai/fluentui-react';
import {
  Delete20Regular,
  Edit20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import type { LocalConnectionKind } from './useConnectionsEditor';

export interface JsonConnectionActionTarget {
  kind: LocalConnectionKind;
  id: string;
  /** Resolved at render time so dialog bodies stay stable across renders. */
  displayName: string;
}

export interface ConnectionActionsMenuProps {
  target: JsonConnectionActionTarget;
  onEdit: (target: JsonConnectionActionTarget) => void;
  onDelete: (target: JsonConnectionActionTarget) => void;
}

export const ConnectionActionsMenu = ({
  target,
  onEdit,
  onDelete,
}: ConnectionActionsMenuProps) => (
  <Menu>
    <MenuTrigger disableButtonEnhancement>
      <Button
        appearance="subtle"
        icon={<MoreHorizontal20Regular />}
        size="medium"
        aria-label={`More actions for ${target.displayName}`}
      />
    </MenuTrigger>
    <MenuPopover>
      <MenuList>
        <MenuItem icon={<Edit20Regular />} onClick={() => onEdit(target)}>
          Edit
        </MenuItem>
        <MenuDivider />
        <MenuItem icon={<Delete20Regular />} onClick={() => onDelete(target)}>
          Delete
        </MenuItem>
      </MenuList>
    </MenuPopover>
  </Menu>
);

ConnectionActionsMenu.displayName = 'ConnectionActionsMenu';
